//customGarment.ts — admin-authored catalogue entries, shared so worker and portal agree

import type { Character } from './character'
import type { Garment } from './garment'
import { GARMENT_SLOTS, type GarmentSlot } from './garmentSlot'

//Characters reuse the garment upload and on/off plumbing; picking them as the live catalogue
//puts every mirror into character mode.
export const GARMENT_CATEGORIES = ['women', 'men', 'decart', 'custom', 'characters'] as const

export type GarmentCategory = (typeof GARMENT_CATEGORIES)[number]

export const CATEGORY_LABEL: Record<GarmentCategory, string> = {
	women: 'Women',
	men: 'Men',
	decart: 'Decart samples',
	custom: 'Custom',
	characters: 'Characters',
}

//The admin picks exactly one live catalogue; the mirror shows only that one.
export type CatalogSource = 'shopify' | GarmentCategory

export const CATALOG_SOURCES: readonly CatalogSource[] = ['shopify', ...GARMENT_CATEGORIES]

export const isCatalogSource = (value: unknown): value is CatalogSource =>
	CATALOG_SOURCES.includes(value as CatalogSource)

export const sourceLabel = (source: CatalogSource): string =>
	source === 'shopify' ? 'Shopify' : CATEGORY_LABEL[source]

export interface CustomGarment {
	readonly id: string
	readonly title: string
	readonly slot: GarmentSlot
	//What Lucy reads. Empty falls back to the title, with server-side enrichment on.
	readonly detail: string
	readonly imageId: string | null
	//Off keeps the garment in the admin library without showing it on the mirror.
	readonly live: boolean
	readonly category: GarmentCategory
}

export const customImagePathFor = (imageId: string): string =>
	`/api/custom-image?id=${encodeURIComponent(imageId)}`

export function toGarment(custom: CustomGarment): Garment {
	return {
		id: custom.id,
		name: custom.title,
		productTitle: custom.title,
		colour: null,
		slot: custom.slot,
		detail: custom.detail.trim() || custom.title,
		imageURL: custom.imageId ? customImagePathFor(custom.imageId) : null,
		enrichPrompt: true,
		priceLabel: null,
		isAvailable: true,
	}
}

export const toCharacter = (custom: CustomGarment): Character => ({
	id: custom.id,
	name: custom.title,
	description: custom.detail,
	imageURL: custom.imageId ? customImagePathFor(custom.imageId) : null,
})

//crypto.randomUUID() output fits; the bound also keeps KV key names sane.
const ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/

export const isValidId = (value: unknown): value is string =>
	typeof value === 'string' && ID_PATTERN.test(value)

//Boundary validation: the endpoint is only a password away from the internet.
export function parseCustomGarments(value: unknown): CustomGarment[] {
	if (!Array.isArray(value)) throw new Error('Expected an array of garments')
	if (value.length > 200) throw new Error('Too many garments (max 200)')
	return value.map((entry, index) => {
		const item = (entry ?? {}) as Record<string, unknown>
		if (!isValidId(item.id)) throw new Error(`Garment ${index + 1}: bad id`)
		if (typeof item.title !== 'string' || !item.title.trim() || item.title.length > 120)
			throw new Error(`Garment ${index + 1}: title must be 1-120 characters`)
		if (!GARMENT_SLOTS.includes(item.slot as GarmentSlot))
			throw new Error(`Garment ${index + 1}: unknown slot "${String(item.slot)}"`)
		if (typeof item.detail !== 'string' || item.detail.length > 400)
			throw new Error(`Garment ${index + 1}: detail must be a string under 400 characters`)
		if (item.imageId !== null && !isValidId(item.imageId))
			throw new Error(`Garment ${index + 1}: bad imageId`)
		if (item.live !== undefined && typeof item.live !== 'boolean')
			throw new Error(`Garment ${index + 1}: live must be true or false`)
		if (item.category !== undefined && !GARMENT_CATEGORIES.includes(item.category as GarmentCategory))
			throw new Error(`Garment ${index + 1}: unknown category "${String(item.category)}"`)
		return {
			id: item.id,
			title: item.title.trim(),
			slot: item.slot as GarmentSlot,
			detail: item.detail,
			imageId: item.imageId as string | null,
			live: item.live !== false,
			category: (item.category as GarmentCategory | undefined) ?? 'custom',
		}
	})
}
